import { memo } from 'react';

import type { IconDefinition, IconName, IconPrefix } from '@fortawesome/fontawesome-common-types';
import classNames from 'classnames';
import type { CSSProperties, JSX } from 'react';

import './Icon.less';

type IconProp = IconDefinition | [IconPrefix, IconName];

interface IconProps {
  icon: IconProp;
  className?: string;
  style?: CSSProperties;
  size?: '1x' | '2x' | '3x' | '4x' | '5x' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl';
  fixedWidth?: boolean;
  color?: string;
  spin?: boolean;
  beat?: boolean;
}

// Icon registry for looking up icons by [prefix, name]
const iconRegistry = new Map<string, IconDefinition>();

// Size mappings matching FontAwesome's sizing
const sizeMap: Record<string, string> = {
  xs: '0.75em',
  sm: '0.875em',
  lg: '1.25em',
  xl: '1.5em',
  '2xl': '2em',
  '1x': '1em',
  '2x': '2em',
  '3x': '3em',
  '4x': '4em',
  '5x': '5em',
};

/**
 * Register an icon for lookup by [IconPrefix, IconName]
 * This is useful when icon data is stored as tuples rather than full definitions
 */
function registerIcon(icon: IconDefinition, prefix: IconPrefix, name: IconName): void {
  iconRegistry.set(`${prefix}-${name}`, icon);
}

/**
 * Register multiple icons at once
 */
export function registerIcons(icons: Array<{ icon: IconDefinition; prefix: IconPrefix; name: IconName }>): void {
  for (const { icon, prefix, name } of icons) {
    registerIcon(icon, prefix, name);
  }
}

/**
 * Lightweight icon component that renders FontAwesome icon definitions directly as SVGs.
 * This eliminates the need for @fortawesome/fontawesome-svg-core (~67 KB).
 *
 * Usage:
 * import { faCircleCheck } from '@awesome.me/kit-24322b77ac/icons/classic/solid';
 * <Icon icon={faCircleCheck} />
 *
 * For [IconPrefix, IconName] format, register icons first:
 * registerIcon(faCircleCheck, 'fas', 'circle-check');
 * <Icon icon={['fas', 'circle-check']} />
 */
export const Icon = memo(function Icon({
  icon,
  className,
  style,
  size = '1x',
  fixedWidth = true,
  color,
  spin = false,
  beat = false,
}: IconProps): JSX.Element {
  let iconDef: IconDefinition;

  // Handle IconProp format - if it's a tuple, look it up in the registry
  if (Array.isArray(icon)) {
    const [prefix, name] = icon;
    const key = `${prefix}-${name}`;
    const registeredIcon = iconRegistry.get(key);

    if (!registeredIcon) {
      // eslint-disable-next-line no-console
      console.warn(`Icon [${prefix}, ${name}] not found in registry. Register it with registerIcon() first.`);
      // Return empty SVG to maintain consistent rendering
      return <svg className={classNames('icon', className)} style={style} aria-hidden="true" />;
    }

    iconDef = registeredIcon;
  } else {
    iconDef = icon;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [width, height, _ligatures, _unicode, pathData] = iconDef.icon;

  const fontSize = sizeMap[size] ?? '1em';

  // Build inline styles only for non-default values
  const inlineStyle: CSSProperties = {};
  if (fontSize !== '1em') {
    inlineStyle.fontSize = fontSize;
  }
  if (!fixedWidth) {
    inlineStyle.width = 'auto';
    inlineStyle.height = 'auto';
  }
  if (color) {
    inlineStyle.color = color;
  }

  return (
    <svg
      className={classNames(
        'icon',
        {
          'icon--spin': spin,
          'icon--beat': beat,
        },
        className,
      )}
      style={{ ...inlineStyle, ...style }}
      viewBox={`0 0 ${width} ${height}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      role="img"
    >
      {Array.isArray(pathData) ? (
        // Multi-path icon (duotone, layered, etc.)
        pathData.map((d, i) => <path key={i} d={d} />)
      ) : (
        // Single path icon
        <path d={pathData} />
      )}
    </svg>
  );
});
